import { erpApi } from "@/shared/axios";
import { urlBackend } from "@/shared/constants";
// DataSourceType y getFidelizationConfigSummary se centralizan en
// migracion.actions para evitar TS2308 al re-exportar `*` desde el barrel
// (./shared/actions/campanas/index.ts). Aqui solo se reexportan.
import { DataSourceType } from "./migracion.actions";

const { get, post } = erpApi();

const FIDELIZATION_BASE_URL = `${urlBackend}api/v1/campanas`;

const buildDataSourceParam = (dataSource?: DataSourceType) => {
  return dataSource ? `&dataSource=${encodeURIComponent(dataSource)}` : "";
};

export interface SelectedCatalogRequest {
  externalId?: string | null;
  availableCatalogId?: number | null;
  name: string;
  source: "ERP" | "LOCAL";
}

export interface FidelizationConfigurationRequest {
  permanenceValue: number;
  permanenceUnit: "YEARS" | "MONTHS";
}

export interface SelectedCatalogResponse {
  id: number;
  externalId?: string | null;
  availableCatalogId?: number | null;
  type: "PLAN" | "EQUIPMENT" | "IMPROVEMENT";
  source: "ERP" | "LOCAL";
  name: string;
}

export interface FidelizationConfigurationResponse {
  parameterId: number;
  permanenceValue: number;
  permanenceUnit: "YEARS" | "MONTHS";
  plans: SelectedCatalogResponse[];
  equipments: SelectedCatalogResponse[];
  improvements: SelectedCatalogResponse[];
}

export interface FidelizationOption {
  id: string | number;
  name: string;
  active?: boolean;
}

export interface FidelizationCandidateResponse {
  candidateId: number;
  identification: string;
  customerName: string;
  planName: string;
  zoneName: string;
  equipmentName: string;
  installationDate: string;
  yearsInService: number;
  monthsInService: number;
  eligible: boolean;
}

export interface PageResponse<T> {
  content: T[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface FidelizationCandidatesFilters {
  zone?: string;
  plan?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ContratoCampanasFiltroRequest {
  fechaDesde: string;
  planNombre?: string;
  estadoContrato?: string;
  formasPagoExcluidas?: string[];
  ciudad?: string;
  tipoPlan?: string;
  fecha_desde?: string;
  plan_nombre?: string;
  estado_contrato?: string;
  formas_pago_excluidas?: string[];
  tipo_plan?: string;
}

export interface FidelizationContractOptionsResponse {
  planes: string[];
  ciudades: string[];
  tiposPlan: string[];
  estadosContrato: string[];
  formasPago: string[];
}

export interface ContratoCampanasFiltroResponse {
  candidateId: number;
  idContrato: string;
  idCliente?: string;
  razonSocial: string;
  tickets: number;
  identificacion?: string;
  contratoEquipoId?: string;
}

export interface CampaignReportEmailRequest {
  to: string[];
  subject: string;
  message: string;
  module: "FIDELIZATION";
  filters: Partial<ContratoCampanasFiltroRequest>;
}

export interface EmailResponse {
  success: boolean;
  message: string;
  totalRecipients: number;
}

export const getFidelizationConfiguration = async () => {
  return await get<FidelizationConfigurationResponse>(
    `${FIDELIZATION_BASE_URL}/fidelization/configuration`,
    true,
  );
};

export const createFidelizationConfiguration = async (
  data: FidelizationConfigurationRequest,
) => {
  return await post<FidelizationConfigurationResponse>(
    `${FIDELIZATION_BASE_URL}/fidelization/configuration`,
    data,
    true,
  );
};

export const getFidelizationCandidates = async (
  page = 0,
  size = 10,
  dataSource?: DataSourceType,
) => {
  return await get<PageResponse<FidelizationCandidateResponse>>(
    `${FIDELIZATION_BASE_URL}/service-candidates/fidelization?page=${page}&size=${size}${buildDataSourceParam(
      dataSource,
    )}`,
    true,
  );
};

export const filterFidelizationCandidates = async (
  page = 0,
  size = 10,
  filters: FidelizationCandidatesFilters,
  dataSource?: DataSourceType,
) => {
  return await post<PageResponse<FidelizationCandidateResponse>>(
    `${FIDELIZATION_BASE_URL}/service-candidates/fidelization/filter?page=${page}&size=${size}${buildDataSourceParam(
      dataSource,
    )}`,
    filters,
    true,
  );
};

// tickets de fidelización desde el mismo modal
export const createFidelizationTicketsFromCandidates = async (payload: {
  candidateIds: number[];
  requestType: "FIDELIZATION";
  targetAreaId: string;
  targetAreaName: string;
}) => {
  return await post<any>(
    `${FIDELIZATION_BASE_URL}/tickets/from-candidates`,
    payload,
    true,
  );
};

export const sendFidelizationCampaignReportEmail = async (
  payload: CampaignReportEmailRequest,
) => {
  return await post<EmailResponse>(
    `${FIDELIZATION_BASE_URL}/emails/send-report`,
    payload,
    true,
  );
};

export const searchFidelizationContracts = async (
  payload: ContratoCampanasFiltroRequest,
) => {
  return await post<ContratoCampanasFiltroResponse[]>(
    `${FIDELIZATION_BASE_URL}/tickets/contracts/fidelization/search`,
    payload,
    true,
  );
};

export const getFidelizationContractOptions = async () => {
  return await get<FidelizationContractOptionsResponse>(
    `${FIDELIZATION_BASE_URL}/tickets/contracts/fidelization/options`,
    true,
  );
};
